import capitalize from 'lodash.capitalize';
import get from 'lodash.get';

// import the api endpoints
import api from '../api';

const PUBLIC_RECORDS_REMOVE_LIMITS_KEY = 'publicRecordsRemoveLimits';
const PUBLIC_RECORDS_REMOVE_EXCLUDE_KEY = 'publicRecordsRemoveExclude';

const initialState = () => ({
    searchTerm: '',
    searchResults: [],
    screens: [],
    currentScreen: {},
    loading: false,
    dataLoading: false,
    screeners: [],
    screenFilters: {},
    pagination: {},
    application: [], // Applications associated with screens from the portal
    currentApplication: {}, // Application associated with a screen
    applicants: [],
    currentApplicant: {},
    screenSearch: {},
    applicantImports: [],
    prints: [],
    states: [],
    removeLimits:
        localStorage.getItem(PUBLIC_RECORDS_REMOVE_LIMITS_KEY) === 'true', // removes crime limits
    removeExclude:
        localStorage.getItem(PUBLIC_RECORDS_REMOVE_EXCLUDE_KEY) === 'true', // removes crime records excluded by student
});

const state = initialState();

const getters = {
    searchTerm: (state) => state.searchTerm,
    searchResults: (state) => state.searchResults,
    screens: (state) => state.screens,
    screeners: (state) => state.screeners,
    screenFilters: (state) => state.screenFilters,
    pagination: (state) => state.pagination,
    currentScreen: (state) => state.currentScreen,
    applications: (state) => state.applications,
    currentApplication: (state) => state.currentApplication,
    applicants: (state) => state.applicants,
    currentApplicant: (state) => state.currentApplicant,
    loading: (state) => state.loading,
    dataLoading: (state) => state.dataLoading,
    screenSearch: (state) => state.screenSearch,
    applicantImports: (state) => state.applicantImports,
    prints: (state) => state.prints,
    states: (state) => state.states,
    removeLimits: (state) => state.removeLimits,
    removeExclude: (state) => state.removeExclude,
};

const actions = {
    reset({ commit }) {
        commit('RESET');
    },
    async setSearchTerm({ commit }, term) {
        if (!term) {
            commit('SET_SEARCH_RESULTS', []);
        }
        commit('SET_SEARCH_TERM', term);
    },
    async getStates({ commit }) {
        commit('SET_DATA_LOADING', true);
        const states = await api.getStates();
        commit('SET_STATES', states);
        commit('SET_DATA_LOADING', false);
    },
    async saveCurrentScreenAddress({ commit }, address) {
        commit('SET_CURRENT_SCREEN_ADDRESS', address);
    },
    async printScreen({ commit }, { id, uuid }) {
        try {
            await api.printScreen(id, uuid);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },
    // Printable route uses a signature from the queryParams
    async getPrintableScreen({ commit }, queryParams) {
        try {
            let { screen, application } = await api.getPrintableScreen(
                queryParams
            );
            commit('SET_CURRENT_SCREEN', screen);
            commit('SET_CURRENT_APPLICATION', application);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },
    async updateSearchCrime({ commit }, crime_id) {
        try {
            commit('SET_LOADING', true);
            const params = {
                exclude: true,
            };
            await api.updateSearchCrimes(crime_id, params);
            let id = 0;
            id = id === 0 ? state.currentScreen.id : id;

            let { screen, application } = await api.getScreen(id, params);
            commit('SET_CURRENT_SCREEN', screen);
            commit('SET_CURRENT_APPLICATION', application);

            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
            commit('SET_LOADING', false);
        }
    },
    async updateSearchEviction({ commit }, eviction_id) {
        try {
            commit('SET_LOADING', true);
            const params = {
                exclude: true,
            };
            await api.updateSearchEvictions(eviction_id, params);
            let id = 0;
            id = id === 0 ? state.currentScreen.id : id;

            let { screen, application } = await api.getScreen(id, params);
            commit('SET_CURRENT_SCREEN', screen);
            commit('SET_CURRENT_APPLICATION', application);

            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
            commit('SET_LOADING', false);
        }
    },

    async getCurrentScreen({ commit }, id = 0) {
        try {
            commit('SET_LOADING', true);
            const params = {
                'remove-limits': state.removeLimits,
                'remove-exclude': state.removeExclude,
            };
            // we can use ID 0 to just refresh current screen
            id = id === 0 ? state.currentScreen.id : id;

            let { screen, application } = await api.getScreen(id, params);
            commit('SET_CURRENT_SCREEN', screen);
            commit('SET_CURRENT_APPLICATION', application);
            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
            commit('SET_LOADING', false);
        }
    },
    async getApplicants({ commit }) {
        try {
            commit('SET_LOADING', true);
            let applicants = await api.getApplicants();
            commit('SET_APPLICANTS', applicants);
            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },
    async getCurrentApplicant({ commit }, id) {
        try {
            commit('SET_LOADING', true);
            let applicant = await api.getApplicant(id);
            commit('SET_CURRENT_APPLICANT', applicant);
            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
            commit('SET_LOADING', false);
        }
    },
    async getApplications({ commit }) {
        try {
            commit('SET_LOADING', true);
            let applications = await api.getApplications();
            commit('SET_APPLICATIONS', applications);
            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },
    async getSearchResults({ commit, dispatch }, term) {
        try {
            if (state.screeners.length === 0) {
                dispatch('Screens/getScreeners', {}, { root: true });
            }
            commit('SET_LOADING', true);
            let { page } = await api.getSearchResults(term);
            commit('SET_SEARCH_RESULTS', page.data);
            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
            commit('SET_LOADING', false);
        }
    },
    async getScreens({ commit }, params) {
        try {
            commit('SET_LOADING', true);

            let { filters, page } = await api.getScreens(
                params.filters,
                params.pagination
            );

            commit('SET_SCREENS', page.data);
            commit('SET_SCREEN_FILTERS', filters);
            commit('SET_PAGINATION', page);

            commit('SET_LOADING', false);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
            commit('SET_LOADING', false);
        }
    },
    async getScreeners({ commit }, count = 10) {
        try {
            let screeners = await api.getScreeners(count);
            commit('SET_SCREENERS', screeners);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },
    async getScreenSearch({ commit }) {
        try {
            let screenSearch = await api.getScreenSearch();
            commit('SET_SCREEN_SEARCH', screenSearch);
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },
    async getApplicantImports({ commit }) {
        try {
            commit('SET_DATA_LOADING', true);
            let applicantImports = await api.getApplicantImports();
            commit('SET_APPLICANT_IMPORTS', applicantImports);
            commit('SET_DATA_LOADING', false);
        } catch (error) {
            commit('SET_DATA_LOADING', false);
            commit('SET_ERROR', error, { root: true });
        }
    },
    async updateAssignedTo({ commit, dispatch }, { order_id, user_id }) {
        try {
            let data = await api.updateAssignedTo(order_id, user_id);

            commit('SET_ASSIGNED_TO', data);

            dispatch(
                'Snackbar/showMessage',
                {
                    content: `Successfully assigned ${data.screen.assigned_to.name}`,
                    color: 'secondary',
                },
                { root: true }
            );
        } catch (error) {
            commit('SET_ERROR', error, { root: true });
        }
    },

    // *** START OF EVENTS FROM PUSHER ****
    async importApplicantError({ commit }, errorData) {
        const { orderId, error } = errorData;
        commit('SET_DATA_LOADING', false);
        commit('SET_ERROR', error, { root: true });

        commit('UPDATE_ORDERS', {
            orderId,
            status: {
                status: 'SCREEN_ERROR',
                label: 'Screen Error',
                color: 'error',
            },
        });
        console.log(
            'Event Import Error with Order = %n, Error = %s',
            orderId,
            error
        );
    },

    async immportApplicantComplete({ commit }, orderId) {
        commit('SET_DATA_LOADING', false);
        console.log('Event Import Complete with Order = %i', orderId);
    },

    async screenProcessError({ commit, dispatch }, errorData) {
        const { order_id: orderId, error } = errorData;
        commit('UPDATE_ORDERS', {
            orderId,
            status: {
                status: 'SCREEN_ERROR',
                label: 'Screen Error',
                color: 'error',
            },
        });
        commit('SET_DATA_LOADING', false);

        dispatch(
            'Snackbar/showMessage',
            {
                content: `Error in processing screen with message ${error.message}`,
                color: 'error',
            },
            { root: true }
        );

        console.log(
            'Event Screen Process Error with Order = %i, Error = %o',
            orderId,
            error
        );
    },

    /**
     * Called from Pusher when we start processing a screen
     * @param {*} param0
     * @param {*} orderId
     */
    async screenProcessStart({ commit }, orderId) {
        commit('SET_DATA_LOADING', false);
        console.log('Event Screen Process Start with Order = %i', orderId);
        const payload = {
            orderId,
            status: {
                status: 'SCREEN_IN_PROCESS',
                label: 'Screen In Process',
                color: 'warning',
            },
        };
        // We need to update orders if we have a list of orders or update the current screen if the same ID
        commit('UPDATE_ORDERS', payload);
        commit('UPDATE_CURRENT_SCREEN', payload);
    },

    /**
     * Called from pusher when we complete processing a screen
     * @param {*} param0
     * @param {*} orderId
     */
    async screenProcessComplete(
        { commit, dispatch },
        { orderId, orderStatus }
    ) {
        console.log(
            'Event Screen Process Complete with Order = %i, Status = %s',
            orderId,
            orderStatus
        );
        commit('SET_DATA_LOADING', false);
        const payload = {
            orderId,
            status: {
                status: orderStatus,
                label: capitalize(orderStatus.replace('_', ' ')),
                color:
                    orderStatus.toLowerCase().search('complete') !== -1
                        ? 'success'
                        : 'warning',
            },
        };
        commit('UPDATE_ORDERS', payload);

        if (get(state.currentScreen, 'order_id', 0) === orderId) {
            dispatch('getCurrentScreen', orderId);
        }
    },

    async screenPrintComplete({ commit }, { printData }) {
        commit('SET_DATA_LOADING', false);
        commit('UPDATE_PRINTS', printData);
        console.log(
            'Event Screen Print Complete with Print Data = %o',
            printData
        );
    },

    async screenPrintError({ commit, dispatch }, { printData }) {
        console.log('Screen print error with %o', printData);
        commit('SET_DATA_LOADING', false);
        commit('UPDATE_PRINTS', printData);
        dispatch(
            'Snackbar/showMessage',
            {
                content: `Error in printing order with message ${printData.error}`,
                color: 'error',
            },
            { root: true }
        );
    },

    async resubmitScreen({ commit }, applicant_id) {
        try {
            commit('SET_DATA_LOADING', true);
            await api.resubmitScreen(applicant_id);
            commit('SET_DATA_LOADING', false);
        } catch (error) {
            commit('SET_DATA_LOADING', false);
            commit('SET_ERROR', error, { root: true });
        }
    },

    async importApplicant({ commit }, importData) {
        try {
            commit('SET_DATA_LOADING', true);
            let applicant = await api.importApplicant(importData);
            console.log(applicant);
            commit('SET_DATA_LOADING', false);
        } catch (error) {
            commit('SET_DATA_LOADING', false);
            commit('SET_ERROR', error, { root: true });
        }
    },

    async deleteMultiple({ commit, dispatch }, order_ids) {
        try {
            commit('SET_DATA_LOADING', true);
            await api.deleteMultiple(order_ids);
            const { per_page } = state.pagination;
            dispatch(
                'Screens/getScreens',
                {
                    filters: state.filters,
                    pagination: {
                        per_page,
                        current_page: 1,
                    },
                },
                { root: true }
            );
        } catch (error) {
            commit('SET_DATA_LOADING', false);
            commit('SET_ERROR', error, { root: true });
        }
    },

    // eslint-disable-next-line no-unused-vars
    async validateInvitedUser({ commit }, query) {
        const data = await api.validateInvitedUser(query);
        return data;
    },
    // eslint-disable-next-line no-unused-vars
    async registerUser({ commit }, params) {
        try {
            commit('SET_DATA_LOADING', true);
            const data = await api.registerUser(params);
            commit('SET_DATA_LOADING', false);
            return {
                error: null,
                data,
            };
        } catch (error) {
            commit('SET_DATA_LOADING', false);
            return {
                error,
            };
        }
    },
};

const mutations = {
    RESET: (state) => {
        const newState = initialState();
        Object.keys(newState).forEach((key) => {
            state[key] = newState[key];
        });
    },
    SET_SCREENS: (state, payload) => {
        state.screens = payload;
    },
    SET_SEARCH_RESULTS: (state, payload) => {
        state.searchResults = payload;
    },
    SET_SEARCH_TERM: (state, payload) => {
        state.searchTerm = payload;
    },

    SET_CURRENT_SCREEN_ADDRESS: (state, payload) => {
        let addresses = state.currentScreen.applicant.addresses;
        const index = addresses.findIndex((item) => item.id === payload.id);
        addresses.splice(index, 1, payload);
        state.currentScreen.applicant.addresses = addresses;
    },
    SET_SCREEN_FILTERS: (state, payload) => {
        state.screenFilters = payload;
    },
    SET_PAGINATION: (state, payload) => {
        state.pagination = payload;
    },
    SET_SCREENERS: (state, payload) => {
        state.screeners = payload;
    },
    SET_APPLICATIONS: (state, payload) => {
        state.applications = payload;
    },
    SET_CURRENT_SCREEN: (state, payload) => {
        state.currentScreen = payload;
    },
    SET_CURRENT_APPLICATION: (state, payload) => {
        state.currentApplication = payload;
    },
    SET_CURRENT_APPLICANT: (state, payload) => {
        state.currentApplicant = payload;
    },
    SET_APPLICANTS: (state, payload) => {
        state.applicants = payload;
    },
    SET_LOADING: (state, payload) => {
        state.loading = payload;
    },
    SET_DATA_LOADING: (state, payload) => {
        state.dataLoading = payload;
    },
    SET_SCREEN_SEARCH: (state, payload) => {
        state.screenSearch = payload;
    },
    SET_APPLICANT_IMPORTS: (state, payload) => {
        state.applicantImports = payload;
    },
    SET_STATES: (state, payload) => {
        state.states = payload;
    },
    UPDATE_PRINTS: (state, printData) => {
        state.prints.push(printData);
    },
    UPDATE_ORDERS: (state, { orderId, status }) => {
        const index = state.screens.findIndex(
            (screen) => screen.order_id === orderId
        );
        if (index !== -1 && index !== null) {
            let screen = Object.assign({}, state.screens[index]);
            screen.status = status;
            state.screens.splice(index, 1, screen);
        }
    },
    UPDATE_CURRENT_SCREEN(state, { orderId, status }) {
        if (get(state.currentScreen, 'order_id', 0) === orderId) {
            state.currentScreen = Object.assign(state.currentScreen, status);
        }
    },

    SET_ASSIGNED_TO: (state, payload) => {
        const index = state.screens.findIndex(
            (item) => item.order_id === payload.id
        );
        let screen = Object.assign({}, state.screens[index]);
        const { name, id } = payload.screen.assigned_to;
        screen.assigned_to = { name, id };
        state.screens.splice(index, 1, screen);
    },
    SET_REMOVE_LIMITS: (state, payload) => {
        state.removeLimits = payload;
        localStorage.setItem(
            PUBLIC_RECORDS_REMOVE_LIMITS_KEY,
            payload.toString()
        );
    },
    SET_REMOVE_EXCLUDE: (state, payload) => {
        state.removeExclude = payload;
        localStorage.setItem(
            PUBLIC_RECORDS_REMOVE_EXCLUDE_KEY,
            payload.toString()
        );
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
